import Search from '@/assets/icons/Search';
import StatusBadge from '@/components/shared/Badge/Badge';
import CardCurrency from '@/components/shared/CardCurrency/CardCurrency';
import { ExportButton } from '@/components/shared/ExportButton';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Command, CommandInput } from '@/components/ui/command';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import LayoutInside from '@/layout/LayoutInside';
import HourlySalesChart from '@/pages/Dashboard/Partials/HourlySalesChart';
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker';
import useModalStore from '@/store/useModalStore';
import { getIconMethodPayment } from '@/utils/getIconMethodPayment';
import { getOfferTypeLabel } from '@/lib/utils';
import { resolveSubscriptionDisplayStatus } from '@/utils/resolveSubscriptionDisplayStatus';
import { ListFilter } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FilterSubscriptions from './Partials/FilterSubscriptions';
import {
  SUBSCRIPTION_CHART_KEYS,
  SUBSCRIPTION_SERIES_CONFIG,
  useSubscriptions,
} from './hooks/useSubscriptions';

export default function Signatures() {
  const { t } = useTranslation(['signatures', 'sales', 'common']);
  const navigate = useNavigate();
  const { modals, openModal, closeModal } = useModalStore();

  const {
    dateRange,
    dateAxis,
    handleDateAxisChange,
    handleDateRangeChange,
    getPeriodText,
    metrics,
    isLoadingMetrics,
    chartData,
    isLoadingChart,
    subscriptions,
    isLoadingSubscriptions,
    isFetchingSubscriptions,
    currentPage,
    searchQuery,
    setCurrentPage,
    setFilterParams,
    handleSearchChange,
    filterBadgeLabel,
    filterVisualParams,
    filterParams,
    hasActiveFiltersBeyondDates,
    removeFilterItem,
    removeFilter,
    clearAllFilters,
    keyLabel,
    currency,
    exportStartDate,
    exportEndDate,
    exportFilterParams,
  } = useSubscriptions();

  const breadcrumbItems = [
    { text: t('signatures:title'), link: '/signatures' },
  ];

  const headers = [
    { title: t('signatures:table.date'), key: 'date' },
    { title: t('signatures:table.product'), key: 'product' },
    { title: t('signatures:table.customer'), key: 'customer' },
    { title: t('signatures:table.method'), key: 'method' },
    { title: t('signatures:table.value'), key: 'value' },
    { title: t('signatures:table.status'), key: 'status' },
  ];

  const MAX_VISIBLE_FILTERS = 15;

  const allFilterBadges = useMemo(() => {
    const badges: Array<{
      key: string;
      group: string;
      label: string;
      onClick: () => void;
    }> = [];

    Object.entries(filterVisualParams).forEach(([key, values]) => {
      if (typeof values === 'string') {
        if (values === '') return;
        badges.push({
          key,
          group: `${keyLabel[key]}:`,
          label: `${filterBadgeLabel[values] || values}`,
          onClick: () => removeFilter(key),
        });
        return;
      }

      if (!Array.isArray(values) || values.length === 0) return;

      if (values.every((item): item is string => typeof item === 'string')) {
        values.forEach((value, index) => {
          badges.push({
            key: `${key}-${index}`,
            group: `${keyLabel[key]}:`,
            label: `${filterBadgeLabel[value] || value}`,
            onClick: () => removeFilterItem(key, index),
          });
        });
        return;
      }

      values.forEach((value, index) => {
        if (typeof value === 'string') return;
        badges.push({
          key: `${key}-${index}`,
          group: `${keyLabel[key]}:`,
          label: `${value.label || value.value || ''}`,
          onClick: () => removeFilterItem(key, index),
        });
      });
    });

    return badges;
  }, [
    filterVisualParams,
    keyLabel,
    filterBadgeLabel,
    removeFilterItem,
    removeFilter,
  ]);

  return (
    <LayoutInside items={breadcrumbItems}>
      <CustomSheet
        open={modals['filter-subscriptions']}
        onClose={() => closeModal('filter-subscriptions')}
      >
        <FilterSubscriptions
          filterValues={filterVisualParams as any}
          dateAxis={dateAxis}
          dateRange={dateRange}
          onDateAxisChange={handleDateAxisChange}
          onDateRangeChange={handleDateRangeChange}
          onFilter={(data) => {
            setCurrentPage(1);
            setFilterParams({
              products:
                data.products?.map((opt) => `${opt.label}:::${opt.value}`) ??
                [],
              offers:
                data.offers?.map((opt) => `${opt.label}:::${opt.value}`) ?? [],
              affiliates:
                data.affiliates?.map(
                  (opt) => `${opt.label}:::${opt.value}`,
                ) ?? [],
              coproducers:
                data.coproducers?.map(
                  (opt) => `${opt.label}:::${opt.value}`,
                ) ?? [],
              payment_methods: data.payment_methods ?? [],
              status: data.status ?? [],
              src: data.src ?? '',
              utm_source: data.utm_source ?? '',
              utm_medium: data.utm_medium ?? '',
              utm_campaign: data.utm_campaign ?? '',
              utm_content: data.utm_content ?? '',
              utm_term: data.utm_term ?? '',
            });
          }}
        />
      </CustomSheet>

      <section>
        <Card className="p-3 justify-between">
          <div className="flex items-center gap-4">
            <div className="flex w-full flex-col md:flex-row justify-start gap-4">
              <div className="flex items-center justify-between flex-wrap max-[410px] gap-4">
                <DateRangePicker
                  defaultValue={dateRange}
                  onChange={handleDateRangeChange}
                />
                <Button
                  className="flex md:hidden"
                  variant="secondary"
                  onClick={() => openModal('filter-subscriptions')}
                >
                  <ListFilter size={24} />
                </Button>
              </div>
              <Command className="w-full">
                <CommandInput
                  defaultValue={searchQuery ?? ''}
                  placeholder={t('common:search_placeholder')}
                  onValueChange={handleSearchChange}
                />
              </Command>
            </div>
            <Button
              className="hidden md:flex"
              variant="secondary"
              onClick={() => openModal('filter-subscriptions')}
            >
              <ListFilter size={24} />
              {t('signatures:filter')}
            </Button>
          </div>

          {hasActiveFiltersBeyondDates && (
            <div className="flex flex-wrap gap-2 mt-4">
              {allFilterBadges.slice(0, MAX_VISIBLE_FILTERS).map((badge) => (
                <Badge
                  key={badge.key}
                  className="flex font-medium min-h-7 items-center hover:opacity-80 hover:bg-[#18181b] gap-4 bg-[#18181b] text-colorText"
                >
                  <div className="flex text-colorText items-center gap-1">
                    <span className="text-muted">{badge.group}</span>
                    {badge.label}
                  </div>
                  <button
                    onClick={badge.onClick}
                    className="text-muted cursor-pointer"
                  >
                    ✕
                  </button>
                </Badge>
              ))}

              {allFilterBadges.length > MAX_VISIBLE_FILTERS && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer rounded bg-[#27272A] px-1.5 py-0.5 text-xs font-semibold flex items-center justify-center">
                      +{allFilterBadges.length - MAX_VISIBLE_FILTERS}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="w-80 p-4 bg-[#18181b] text-colorText border border-[#333]">
                    <h4 className="font-semibold mb-2">{t('sales:filters')}</h4>
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto px-4">
                      {allFilterBadges
                        .slice(MAX_VISIBLE_FILTERS)
                        .map((badge) => (
                          <div
                            key={badge.key}
                            className="flex items-center justify-between text-sm"
                          >
                            <div className="gap-1 flex items-start">
                              <span className="text-muted">{badge.group}</span>
                              <span>{badge.label}</span>
                            </div>
                            <button
                              onClick={badge.onClick}
                              className="text-muted hover:text-white ml-2"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}

              <Button
                variant="secondary"
                onClick={clearAllFilters}
                className="ml-2 h-7 flex items-center gap-4 bg-transparent text-interactive"
              >
                {t('common:clear_all_filters')}
              </Button>
            </div>
          )}
        </Card>

        <section className="grid w-full gap-6 grid-cols-1 md:grid-cols-3 mt-6">
          <CardCurrency
            text={t('signatures:active_subscriptions')}
            subtitle={getPeriodText(dateRange)}
            total={metrics?.active?.count}
            hasTotal
            value={String(metrics?.active?.count ?? 0)}
            status="paid"
            isLoading={isLoadingMetrics}
          />
          <CardCurrency
            text={t('signatures:monthly_recurring')}
            subtitle={getPeriodText(dateRange)}
            total={metrics?.recurring_revenue?.count}
            hasTotal
            value={metrics?.recurring_revenue?.total ?? 'R$ 0,00'}
            status="pending"
            isLoading={isLoadingMetrics}
          />
          <CardCurrency
            text={t('signatures:cancelation_rate')}
            value={`${metrics?.cancellation_rate?.percentage ?? 0}%`}
            status="refused"
            isLoading={isLoadingMetrics}
            percentage={metrics?.cancellation_rate?.percentage}
          />
        </section>
      </section>

      <section className="mt-6">
        <HourlySalesChart
          width="w-full"
          isLoadingData={isLoadingChart}
          text={getPeriodText(dateRange)}
          hourlySales={chartData}
          title={t('signatures:subscriptions')}
          seriesKeys={SUBSCRIPTION_CHART_KEYS}
          seriesConfig={SUBSCRIPTION_SERIES_CONFIG}
          seriesTranslationNs="signatures"
        />

        <Card className="w-full mt-6 overflow-x-auto bg-[#18181B]">
          <div className="p-4 flex items-start justify-between gap-4 flex-col md:flex-row">
            <h2 className="text-lg font-medium">{t('signatures:history')}</h2>
            <ExportButton
              type="subscriptions"
              filters={{
                startDate: exportStartDate,
                endDate: exportEndDate,
                dateAxis,
                filterParams: exportFilterParams,
                q: searchQuery,
                currency,
              }}
              label={t('signatures:export')}
              variant="secondary"
              disabled={subscriptions?.data?.length === 0}
            />
          </div>

          <TableListCustom
            headers={headers}
            isLoadingData={isLoadingSubscriptions || isFetchingSubscriptions}
            data={subscriptions}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            emptyIconMessage={<Search />}
            emptyTitleMessage={t('common:noLogs')}
            emptyDescriptionMessage={t('common:noProductsDescription')}
          >
            {subscriptions?.data?.map((sub) => {
              const displayStatus = resolveSubscriptionDisplayStatus(sub, (key) =>
                t(`signatures:status.${key}`, { defaultValue: key }),
              );

              return (
              <TableRow
                key={sub.public_id}
                onClick={() =>
                  navigate(`/signatures/subscriptions-details/${sub.public_id}`)
                }
                className="hover:bg-secondary h-16 text-sm border-t border-zinc-800 cursor-pointer"
              >
                <TableCell>
                  <span className="text-[#A1A1AA]">
                    {sub.started_at_formatted}
                  </span>
                </TableCell>
                <TableCell>
                  <p className="text-colorText">{sub.product_name}</p>
                  <p className="text-muted">
                    {[
                      sub.offer_type
                        ? getOfferTypeLabel(sub.offer_type)
                        : null,
                      sub.interval_label_key
                        ? t(`common:paymentFrequency.${sub.interval_label_key}`)
                        : sub.offer_name,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </TableCell>
                <TableCell>
                  <p className="text-muted">{sub.customer_name}</p>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getIconMethodPayment(
                      sub.payment_method?.type ?? '',
                      sub.payment_method?.brand,
                    )}
                    <span className="text-muted">
                      {sub.payment_method?.type
                        ? t(`common:${sub.payment_method.type}`)
                        : '-'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-muted">{sub.amount_formatted}</p>
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={displayStatus.status}
                    text={displayStatus.text}
                  />
                </TableCell>
              </TableRow>
              );
            })}
          </TableListCustom>
        </Card>
      </section>
    </LayoutInside>
  );
}
